import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  AlertTriangle,
  CheckCircle2,
  Copy,
  Globe,
  Key,
  type LucideIcon,
  PhoneCall,
  RefreshCw,
  Settings as SettingsIcon,
  Shield,
  Wrench,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { apiService } from '../../services/api'
import type { AxiosLikeError, TelephonySyncState } from '../../types/aicall'

interface UserInfo {
  first_name: string
  last_name: string
  email: string
  subscription: {
    tier: string
    character_count: number
    character_limit: number
  }
}

type ConnectionStatus = 'checking' | 'connected' | 'error'

function getApiErrorMessage(error: unknown, fallback: string) {
  const knownError = error as AxiosLikeError
  const detail =
    typeof knownError.response?.data?.error === 'string'
      ? knownError.response.data.error
      : knownError.response?.data?.error?.reason ||
        knownError.response?.data?.error?.message ||
        knownError.response?.data?.error?.detail

  return knownError.response?.data?.message || detail || knownError.message || fallback
}

async function copyToClipboard(value: string, message: string) {
  if (!value) return
  await navigator.clipboard.writeText(value)
  toast.success(message)
}

function getConnectionStatusText(status: ConnectionStatus) {
  switch (status) {
    case 'connected':
      return 'Conectado'
    case 'error':
      return 'Com falha'
    default:
      return 'Verificando'
  }
}

function StatusDot({ status }: { status: ConnectionStatus }) {
  const color =
    status === 'connected'
      ? 'bg-success-500'
      : status === 'error'
        ? 'bg-danger-500'
        : 'bg-warning-500'

  return <span className={`h-3 w-3 rounded-full ${color} ${status === 'checking' ? 'animate-pulse' : ''}`} />
}

function Panel({
  icon: Icon,
  title,
  subtitle,
  children,
}: {
  icon: LucideIcon
  title: string
  subtitle?: string
  children: ReactNode
}) {
  return (
    <section className="surface">
      <div className="panel-header">
        <div className="flex items-center gap-3">
          <Icon className="h-5 w-5 text-primary-600" />
          <div>
            <h2 className="panel-title">{title}</h2>
            {subtitle ? <p className="panel-subtitle">{subtitle}</p> : null}
          </div>
        </div>
      </div>
      <div className="p-4 sm:p-5">{children}</div>
    </section>
  )
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-slate-100 bg-slate-50 p-3">
      <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</dt>
      <dd className="mt-1 break-words text-sm font-semibold text-slate-950">{value}</dd>
    </div>
  )
}

function ActionRow({
  title,
  description,
  actionLabel,
  onAction,
  disabled = false,
}: {
  title: string
  description: string
  actionLabel: string
  onAction: () => void
  disabled?: boolean
}) {
  return (
    <div className="flex flex-col gap-3 rounded-lg border border-slate-100 bg-slate-50 p-3 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <h3 className="text-sm font-semibold text-slate-950">{title}</h3>
        <p className="text-sm text-slate-500">{description}</p>
      </div>
      <button type="button" className="btn-secondary sm:h-9" onClick={onAction} disabled={disabled}>
        {actionLabel}
      </button>
    </div>
  )
}

export default function Settings() {
  const navigate = useNavigate()
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null)
  const [loading, setLoading] = useState(true)
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('checking')
  const [telephonySync, setTelephonySync] = useState<TelephonySyncState | null>(null)
  const [telephonyLoading, setTelephonyLoading] = useState(false)

  const loadUserInfo = async () => {
    try {
      setLoading(true)
      const response = await apiService.getUserInfo()
      setUserInfo(response.data.data)
    } catch (error) {
      console.error('Erro ao carregar informações do usuário:', error)
      toast.error(getApiErrorMessage(error, 'Erro ao carregar informações da conta.'))
    } finally {
      setLoading(false)
    }
  }

  const checkConnection = async () => {
    try {
      setConnectionStatus('checking')
      const response = await apiService.testConnection()
      setConnectionStatus(response.data.success ? 'connected' : 'error')
      if (response.data.success) {
        toast.success('Conexão com ElevenLabs validada.')
      }
    } catch (error) {
      console.error('Erro ao verificar conexão:', error)
      setConnectionStatus('error')
      toast.error(getApiErrorMessage(error, 'Falha ao verificar conexão com ElevenLabs.'))
    }
  }

  const loadTelephonySync = async () => {
    try {
      setTelephonyLoading(true)
      const response = await apiService.getTelephonySync()
      setTelephonySync(response.data.data)
    } catch (error) {
      console.error('Erro ao carregar sincronia de telefonia:', error)
      toast.error(getApiErrorMessage(error, 'Erro ao carregar sincronia de telefonia.'))
    } finally {
      setTelephonyLoading(false)
    }
  }

  useEffect(() => {
    void loadUserInfo()
    void checkConnection()
    void loadTelephonySync()
  }, [])

  const telephonyIssues = telephonySync?.issues || []
  const telephonyErrors = telephonyIssues.filter((issue) => issue.severity === 'error')
  const telephonyWarnings = telephonyIssues.filter((issue) => issue.severity === 'warning')
  const wavoipDevice = telephonySync?.configuration?.wavoip?.device

  const diagnosticsJson = useMemo(
    () =>
      JSON.stringify(
        {
          checked_at: new Date().toISOString(),
          connection_status: connectionStatus,
          telephony_sync: telephonySync,
        },
        null,
        2
      ),
    [connectionStatus, telephonySync]
  )

  if (loading) {
    return (
      <div className="flex min-h-96 items-center justify-center">
        <RefreshCw className="h-10 w-10 animate-spin text-primary-600" />
      </div>
    )
  }

  return (
    <div className="page-shell">
      <div className="page-header">
        <div>
          <h1 className="page-title">Configurações</h1>
          <p className="page-description">
            Saúde operacional, conta, segurança e atalhos para áreas críticas do workspace.
          </p>
        </div>
        <button type="button" onClick={loadTelephonySync} className="btn-secondary" disabled={telephonyLoading}>
          <RefreshCw className={`h-4 w-4 ${telephonyLoading ? 'animate-spin' : ''}`} />
          Atualizar diagnóstico
        </button>
      </div>

      {/* ElevenLabs connection surface */}
      <section className="surface">
        <div className="panel-header">
          <div className="flex items-center gap-3">
            <Globe className="h-5 w-5 text-primary-600" />
            <div>
              <h2 className="panel-title">Conexão ElevenLabs</h2>
              <p className="panel-subtitle">Estado da API e autenticação ativa.</p>
            </div>
          </div>
        </div>
        <div className="space-y-4 p-4 sm:p-5">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <StatusDot status={connectionStatus} />
              <span className="text-sm text-slate-700">
                ElevenLabs API:{' '}
                <strong
                  className={
                    connectionStatus === 'connected'
                      ? 'text-success-700'
                      : connectionStatus === 'error'
                        ? 'text-danger-700'
                        : 'text-warning-700'
                  }
                >
                  {getConnectionStatusText(connectionStatus)}
                </strong>
              </span>
            </div>
            <button type="button" onClick={checkConnection} className="btn-secondary">
              Testar conexão
            </button>
          </div>

          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div className="metric-card">
              <p className="text-xs font-medium text-slate-500">Erros críticos</p>
              <p className="mt-1 text-2xl font-bold tabular-nums text-slate-950">{telephonyErrors.length}</p>
              <p className="mt-0.5 text-xs text-slate-400">Pendências</p>
            </div>
            <div className="metric-card">
              <p className="text-xs font-medium text-slate-500">Alertas</p>
              <p className="mt-1 text-2xl font-bold tabular-nums text-slate-950">{telephonyWarnings.length}</p>
              <p className="mt-0.5 text-xs text-slate-400">Recomendados</p>
            </div>
            <div className="metric-card">
              <p className="text-xs font-medium text-slate-500">Dispositivo</p>
              <p className="mt-1 truncate text-sm font-bold text-slate-950">{wavoipDevice?.device_name || '—'}</p>
              <p className="mt-0.5 text-xs text-slate-400">{wavoipDevice?.status || 'Sem status'}</p>
            </div>
            <div className="metric-card">
              <p className="text-xs font-medium text-slate-500">Plano</p>
              <p className="mt-1 truncate text-sm font-bold text-slate-950">{userInfo?.subscription.tier || '—'}</p>
              <p className="mt-0.5 text-xs text-slate-400">Assinatura ativa</p>
            </div>
          </div>
        </div>
      </section>

      <section className="surface">
        <div className="panel-header">
          <div className="flex items-start gap-3">
            <PhoneCall className="mt-0.5 h-5 w-5 text-primary-600" />
            <div>
              <h2 className="panel-title">Telefonia e SIP</h2>
              <p className="panel-subtitle">Estado da instância, tronco e alinhamento com ElevenLabs.</p>
            </div>
          </div>
        </div>

        <div className="space-y-4 p-4 sm:p-5">
          <div className="grid gap-4 md:grid-cols-3">
            <div className="rounded-lg border border-slate-200 bg-white p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Instância</p>
              <p className="mt-2 break-words text-sm font-semibold text-slate-950">
                {telephonySync?.configuration?.wavoip?.instance_name || 'Não configurada'}
              </p>
              <p className="mt-1 break-words text-sm text-slate-500">
                {telephonySync?.configuration?.wavoip?.phone_number || 'Sem telefone principal'}
              </p>
            </div>
            <div className="rounded-lg border border-slate-200 bg-white p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Wavoip</p>
              <p className="mt-2 break-words text-sm font-semibold text-slate-950">
                {wavoipDevice?.status || 'Sem status'}
              </p>
              <p className="mt-1 break-words text-sm text-slate-500">
                {wavoipDevice?.device_name || 'Device não identificado'}
              </p>
            </div>
            <div className="rounded-lg border border-slate-200 bg-white p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Tronco SIP</p>
              <p className="mt-2 break-words text-sm font-semibold text-slate-950">
                {telephonySync?.configuration?.wavoip?.sip_host || 'Não informado'}
              </p>
              <p className="mt-1 break-words text-sm text-slate-500">
                Transporte: {telephonySync?.configuration?.elevenlabs?.expected_sip_transport || 'N/A'}
              </p>
            </div>
          </div>

          <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
            {telephonyErrors.length === 0 && telephonyWarnings.length === 0 ? (
              <div className="flex items-center gap-2 text-sm font-medium text-success-700">
                <CheckCircle2 className="h-4 w-4" />
                Diagnóstico crítico sem erros.
              </div>
            ) : (
              <div className="space-y-2">
                {telephonyIssues.slice(0, 6).map((issue) => (
                  <div key={`${issue.code}-${issue.message}`} className="flex min-w-0 items-start gap-2 text-sm text-slate-700">
                    <AlertTriangle
                      className={`mt-0.5 h-4 w-4 flex-none ${
                        issue.severity === 'error'
                          ? 'text-danger-600'
                          : issue.severity === 'info'
                            ? 'text-primary-600'
                            : 'text-warning-600'
                      }`}
                    />
                    <div className="min-w-0">
                      <span className="break-all font-semibold">{issue.code}</span>
                      <span className="mt-1 block break-words sm:ml-2 sm:mt-0 sm:inline">{issue.message}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <Panel icon={Globe} title="Conectividade" subtitle="Validação direta da API e atalhos de integração.">
          <div className="space-y-4">
            <ActionRow
              title="Integrações do usuário"
              description="Gerenciar API keys, webhooks e MCP do workspace atual."
              actionLabel="Abrir integrações"
              onAction={() => navigate('/app/developer')}
            />
            <ActionRow
              title="Números e origens"
              description="Revisar telefones disponíveis, números de provedor e vínculos com agentes."
              actionLabel="Abrir números"
              onAction={() => navigate('/app/phone-numbers')}
            />
          </div>
        </Panel>

        {userInfo ? (
          <Panel icon={Key} title="Conta" subtitle="Dados da assinatura operacional atual.">
            <dl className="grid gap-3 sm:grid-cols-2">
              <Detail label="Nome" value={`${userInfo.first_name} ${userInfo.last_name}`.trim()} />
              <Detail label="E-mail" value={userInfo.email} />
              <Detail label="Plano" value={userInfo.subscription.tier} />
              <Detail
                label="Uso de caracteres"
                value={`${userInfo.subscription.character_count.toLocaleString('pt-BR')} / ${userInfo.subscription.character_limit.toLocaleString('pt-BR')}`}
              />
            </dl>
          </Panel>
        ) : null}
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <Panel icon={SettingsIcon} title="Operação" subtitle="Ações úteis para suporte e revisão de ambiente.">
          <div className="space-y-4">
            <ActionRow
              title="Atualizar estado da telefonia"
              description="Recarrega sincronização, device Wavoip e checagens de SIP."
              actionLabel={telephonyLoading ? 'Atualizando...' : 'Atualizar agora'}
              onAction={() => {
                void loadTelephonySync()
              }}
              disabled={telephonyLoading}
            />
            <ActionRow
              title="Copiar diagnóstico técnico"
              description="Exporta JSON com estado de conexão e sincronização para suporte e debug."
              actionLabel="Copiar JSON"
              onAction={() => {
                void copyToClipboard(diagnosticsJson, 'Diagnóstico copiado.')
              }}
            />
            <ActionRow
              title="Abrir chamadas"
              description="Ir para a fila operacional de ligações e falhas recentes."
              actionLabel="Abrir chamadas"
              onAction={() => navigate('/app/calls')}
            />
          </div>
        </Panel>

        <Panel icon={Shield} title="Segurança e governança" subtitle="Controles administrativos e inspeção do tenant.">
          <div className="space-y-4">
            <ActionRow
              title="Painel administrativo"
              description="Usuários, papéis, cobrança, auditoria e conexões globais."
              actionLabel="Abrir admin"
              onAction={() => navigate('/app/admin')}
            />
            <ActionRow
              title="Pipeline comercial"
              description="Leads por número, colunas e movimentação operacional."
              actionLabel="Abrir pipeline"
              onAction={() => navigate('/app/pipeline')}
            />
            <ActionRow
              title="Conversas e transcrições"
              description="Histórico, resumos e revisão de chamadas concluídas."
              actionLabel="Abrir conversas"
              onAction={() => navigate('/app/conversations')}
            />
          </div>
        </Panel>
      </section>

      <section className="surface">
        <div className="panel-header">
          <div className="flex items-center gap-3">
            <Wrench className="h-5 w-5 text-primary-600" />
            <div>
              <h2 className="panel-title">Resumo técnico</h2>
              <p className="panel-subtitle">JSON de estado para suporte e debug.</p>
            </div>
          </div>
          <button
            type="button"
            className="btn-secondary"
            onClick={() => {
              void copyToClipboard(diagnosticsJson, 'Resumo técnico copiado.')
            }}
          >
            <Copy className="h-4 w-4" />
            Copiar
          </button>
        </div>

        <div className="p-4 sm:p-5">
          <pre className="overflow-x-auto rounded-lg bg-slate-900 px-4 py-3 text-xs leading-5 text-green-400">
            {diagnosticsJson}
          </pre>
        </div>
      </section>
    </div>
  )
}
